import React from 'react';
import { CheckCircle, Paintbrush, Wand2, Images } from 'lucide-react';

// Feature Card Component
const FeatureCard = ({ icon: Icon, title, description }) => (
  <div className="p-6 rounded-xl transition-all duration-300 hover:-translate-y-1 bg-white bg-opacity-10 backdrop-blur-lg border border-white border-opacity-20">
    <div className="text-purple-400 text-4xl mb-4 text-center sm:text-left">
      <Icon size={36} />
    </div>
    <h3 className="text-xl font-bold mb-3 text-center sm:text-left">{title}</h3>
    <p className="text-gray-300 text-center sm:text-left">{description}</p>
  </div>
);

// Feature List Item Component
const FeatureListItem = ({ children }) => (
  <li className="flex items-center">
    <CheckCircle className="text-purple-400 mr-3" size={20} />
    {children}
  </li>
);

// Hero Section Component
const Hero = () => (
  <header className="bg-[#fc7245]">
    <div className="container mx-auto px-4 sm:px-6 py-8 sm:py-16">
      <nav className="flex flex-col sm:flex-row justify-between items-center mb-8 sm:mb-16 space-y-4 sm:space-y-0">
        <div className="text-xl sm:text-2xl font-bold">The Lazy Artist AI</div>
        <a href="https://www.thelazyartistai.com/" 
           className="px-4 sm:px-6 py-2 rounded-full hover:bg-white hover:text-purple-600 transition-all w-full sm:w-auto text-center bg-white bg-opacity-10 backdrop-blur-lg border border-white border-opacity-20">
          Visit Website
        </a>
      </nav>
      
      <div className="flex flex-col md:flex-row items-center justify-between">
        <div className="md:w-1/2 mb-8 md:mb-0 text-center md:text-left">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 sm:mb-6">Unleash Your Creative Power</h1>
          <p className="text-lg sm:text-xl mb-6 sm:mb-8">Experience AI image creation that outperforms Midjourney on all fronts. Transform your Discord server into an art studio.</p>
          <a href="https://discord.com/oauth2/authorize?client_id=1282040578120355871" 
             className="inline-block bg-white text-purple-600 px-6 sm:px-8 py-3 rounded-full font-bold hover:bg-purple-100 transition-all w-full sm:w-auto text-center">
            Add to Discord
          </a>
        </div>
        <div className="md:w-1/2 flex justify-center px-4 sm:px-0">
          <img src="https://storage.googleapis.com/tla-filestorage/LXOHJjj2ShB0eN7AZ0-0" alt="AI Art Example" className="rounded-lg shadow-2xl w-full max-w-md" />
        </div>
      </div>
    </div>
  </header>
);

// Features Section Component
const Features = () => (
  <section className="container mx-auto px-4 sm:px-6 py-12 sm:py-20">
    <h2 className="text-3xl sm:text-4xl font-bold text-center mb-10 sm:mb-16">Powerful Features</h2>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
      <FeatureCard 
        icon={Paintbrush}
        title="Advanced Inpainting"
        description="Seamlessly edit and enhance specific parts of your images with precise control."
      />
      <FeatureCard 
        icon={Wand2}
        title="Flux Technology"
        description="Our unique Flux system ensures consistent, high-quality results every time."
      />
      <FeatureCard 
        icon={Images}
        title="Image-to-Image"
        description="Transform existing images into new artworks while maintaining core elements."
      />
    </div>
  </section>
);

// More Features Section Component
const MoreFeatures = () => (
  <section className="bg-gray-800 py-12 sm:py-20">
    <div className="container mx-auto px-4 sm:px-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
        <div className="text-center md:text-left">
          <h2 className="text-2xl sm:text-3xl font-bold mb-6">Everything You Need</h2>
          <ul className="space-y-4 inline-block text-left">
            <FeatureListItem>Stable Diffusion-Based Generation</FeatureListItem>
            <FeatureListItem>Professional Upscaling</FeatureListItem>
            <FeatureListItem>Smart Image Variation</FeatureListItem>
            <FeatureListItem>AI-Powered Prompt Enhancement</FeatureListItem>
          </ul>
        </div>
        <div className="flex justify-center mt-6 md:mt-0">
          <img src="https://storage.googleapis.com/tla-filestorage/kB4w5CM3B5A456YpmG-0" alt="Feature Showcase" className="rounded-lg shadow-xl w-full max-w-md" />
        </div>
      </div>
    </div>
  </section>
);

// CTA Section Component
const CTASection = () => (
  <section className="bg-gradient-to-r from-indigo-500 to-purple-500 py-12 sm:py-20">
    <div className="container mx-auto px-4 sm:px-6 text-center">
      <h2 className="text-3xl sm:text-4xl font-bold mb-6 sm:mb-8">Retake Your Imagination Today</h2>
      <p className="text-lg sm:text-xl mb-8 sm:mb-12 max-w-2xl mx-auto">
        Join thousands of creators who have already discovered the power of The Lazy Artist AI. 
        Transform your Discord server into an AI-powered art studio.
      </p>
      <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-6">
        <a href="https://discord.com/oauth2/authorize?client_id=1282040578120355871" 
           className="bg-white text-purple-600 px-6 sm:px-8 py-3 rounded-full font-bold hover:bg-purple-100 transition-all">
          Add to Discord
        </a>
        <a href="https://www.thelazyartistai.com/" 
           className="bg-white bg-opacity-10 backdrop-blur-lg border border-white border-opacity-20 px-6 sm:px-8 py-3 rounded-full hover:bg-white hover:text-purple-600 transition-all">
          Learn More
        </a>
      </div>
    </div>
  </section>
);

// Footer Component
const Footer = () => (
  <footer className="bg-gray-900 py-8 sm:py-12">
    <div className="container mx-auto px-4 sm:px-6 text-center text-gray-400">
      <p>© 2024 The Lazy Artist AI. All rights reserved.</p>
    </div>
  </footer>
);

// Main App Component
const App = () => {
  return (
    <div className="bg-gray-900 text-white">
      <Hero />
      <Features />
      <MoreFeatures />
      <CTASection />
      <Footer />
    </div>
  );
};

export default App;